import { createContext, useContext, useState, useEffect } from 'react';
import admin from '../api/admin';
import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: "aes" });
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    token: null,
    user: null
  });
  const [loading, setLoading] = useState(true);

  // Centralized token getter - checks all possible locations
  const getStoredToken = () => {
    try {
      // First try SecureLS
      const secureToken = ls.get('token');
      if (secureToken) return secureToken;
      
      // Fallback to SecureLS authToken
      const secureAuthToken = ls.get('authToken');
      if (secureAuthToken) return secureAuthToken;
    } catch (e) {
      console.warn('SecureLS access failed:', e.message);
    }
    
    // Fallback to localStorage
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  };

  // Centralized user data getter
  const getStoredUserData = () => {
    try {
      // First try SecureLS
      const secureUserData = ls.get('userData');
      if (secureUserData) return secureUserData;
    } catch (e) {
      console.warn('SecureLS userData access failed:', e.message);
    }
    
    // Fallback to localStorage
    const localUserData = localStorage.getItem('userData');
    if (localUserData) {
      try {
        return JSON.parse(localUserData);
      } catch (parseError) {
        console.error('Error parsing stored user data:', parseError);
      }
    }
    
    return null;
  };

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const decodedToken = JSON.parse(window.atob(base64));
      
      // Add 1 minute buffer to account for clock differences
      const now = Date.now() / 1000;
      const expiry = decodedToken.exp;
      
      return expiry <= now + 60; // 1 minute buffer
    } catch (e) {
      console.error('Error checking token expiration:', e);
      return true;
    }
  };

  // Clear all stored auth data
  const clearAuthData = () => {
    try {
      // Clear SecureLS
      ls.remove('token');
      ls.remove('authToken');
      ls.remove('userData');
    } catch (e) {
      console.warn('SecureLS cleanup failed:', e.message);
    }
    
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
  };

  // Verify token with server
  const verifyTokenWithServer = async (token) => {
    try {
      const response = await fetch('http://localhost:7002/api/admin/verify-token', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        return { valid: true, user: data.user };
      } else {
        console.warn('Token verification failed with server:', response.status);
        return { valid: false };
      }
    } catch (error) {
      console.error('Token verification network error:', error);
      return { valid: false };
    }
  };

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      
      try {
        const token = getStoredToken();
        const userData = getStoredUserData();
        
        if (!token) {
          setAuth({
            isAuthenticated: false,
            token: null,
            user: null
          });
          return;
        }

        // Check if token is expired
        if (isTokenExpired(token)) {
          clearAuthData();
          setAuth({
            isAuthenticated: false,
            token: null,
            user: null
          });
          return;
        }

        // If we have userData cached and token is not expired, use it
        if (userData) {
          setAuth({
            isAuthenticated: true,
            token,
            user: userData
          });
          
          // Optionally verify with server in background (don't block UI)
          verifyTokenWithServer(token).then(result => {
            if (!result.valid) {
              logout();
            }
          });
          return;
        }

        // If no cached user data, verify with server
        const verification = await verifyTokenWithServer(token);
        
        if (verification.valid) {
          const authenticatedState = {
            isAuthenticated: true,
            token,
            user: verification.user
          };
          
          setAuth(authenticatedState);
          
          // Store user data for next time
          try {
            ls.set('userData', verification.user);
            localStorage.setItem('userData', JSON.stringify(verification.user));
          } catch (e) {
            console.warn('Failed to cache user data:', e.message);
          }
        } else {
          clearAuthData();
          setAuth({
            isAuthenticated: false,
            token: null,
            user: null
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuthData();
        setAuth({
          isAuthenticated: false,
          token: null,
          user: null
        });
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (token, userData) => {
    if (!token || !userData?.data?.user) {
      console.error('Invalid login data provided');
      return;
    }

    const user = userData.data.user;
    
    try {
      // Store in both SecureLS and localStorage for redundancy
      ls.set('token', token);
      ls.set('authToken', token);
      ls.set('userData', user);
      
      localStorage.setItem('token', token);
      localStorage.setItem('authToken', token);
      localStorage.setItem('userData', JSON.stringify(user));
      
      setAuth({
        isAuthenticated: true,
        token,
        user
      });
    } catch (error) {
      console.error('Error in login function:', error);
    }
  };

  const logout = () => {
    clearAuthData();
    setAuth({
      isAuthenticated: false,
      token: null,
      user: null
    });
  };

  // Provide auth context
  return (
    <AuthContext.Provider value={{ 
      user: auth.user, 
      token: auth.token,
      isAuthenticated: auth.isAuthenticated,
      login, 
      logout, 
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};